import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiUsers, HiDocumentText, HiCurrencyDollar, HiClock, HiArrowRight } from 'react-icons/hi';
import Layout from '../components/Layout';
import { supabase } from '../supabase/client';
import { useAuth } from '../context/AuthContext';
import { formatAmount, formatDate } from '../utils/format';
import toast from 'react-hot-toast';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const AVATAR_COLORS = [
  'bg-indigo-500', 'bg-violet-500', 'bg-pink-500',
  'bg-blue-500',   'bg-emerald-500','bg-orange-500',
];
const avatarColor = (name) => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

const CLIENT_STATUS_STYLE = {
  Active:   'bg-green-100 text-green-700',
  Inactive: 'bg-gray-100  text-gray-500',
};

const INVOICE_STATUS_STYLE = {
  Paid:    'bg-green-100  text-green-700',
  Pending: 'bg-yellow-100 text-yellow-700',
  Overdue: 'bg-red-100    text-red-600',
  Draft:   'bg-gray-100   text-gray-500',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ icon: Icon, iconBg, iconColor, value, label, sub, trend, trendUp, loading }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-4">
      <div className="flex items-start justify-between">
        <div className={`${iconBg} p-3 rounded-xl`}>
          <Icon className={`w-6 h-6 ${iconColor}`} />
        </div>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
          trendUp ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'
        }`}>
          {trend}
        </span>
      </div>
      <div>
        {loading
          ? <div className="h-8 w-16 bg-gray-200 rounded-lg animate-pulse mb-2" />
          : <p className="text-2xl font-bold text-gray-900 leading-none mb-1">{value}</p>
        }
        <p className="text-sm font-medium text-gray-600">{label}</p>
        <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, sub }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center px-6">
      <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-3">
        <Icon className="w-7 h-7 text-gray-400" />
      </div>
      <p className="text-sm font-semibold text-gray-600">{title}</p>
      <p className="text-xs text-gray-400 mt-1">{sub}</p>
    </div>
  );
}

function RowSkeleton() {
  return (
    <div className="flex items-center gap-3 px-6 py-3.5 animate-pulse">
      <div className="w-9 h-9 rounded-full bg-gray-200 flex-shrink-0" />
      <div className="flex-1 space-y-2 min-w-0">
        <div className="h-3.5 bg-gray-200 rounded-full w-1/3" />
        <div className="h-3   bg-gray-100 rounded-full w-1/2" />
      </div>
      <div className="h-5 bg-gray-100 rounded-full w-14 flex-shrink-0" />
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { user }  = useAuth();
  const navigate  = useNavigate();

  // Data state
  const [clients,       setClients]       = useState([]);
  const [clientCount,   setClientCount]   = useState(0);
  const [invoices,      setInvoices]      = useState([]);   // recent 4
  const [invoiceCount,  setInvoiceCount]  = useState(0);
  const [revenue,       setRevenue]       = useState(0);
  const [pending,       setPending]       = useState(0);
  const [loading,       setLoading]       = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);

    // Run both queries in parallel
    const [clientRes, invoiceStatsRes, invoiceRecentRes] = await Promise.all([
      // Latest 4 clients + total count
      supabase
        .from('clients')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(4),

      // All invoices (status + amount) for stat calculations
      supabase
        .from('invoices')
        .select('status, amount')
        .eq('user_id', user.id),

      // Latest 4 invoices with client name for the table
      supabase
        .from('invoices')
        .select('id, invoice_number, amount, status, due_date, clients(name)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(4),
    ]);

    if (clientRes.error)       toast.error('Failed to load clients');
    if (invoiceStatsRes.error) toast.error('Failed to load invoice stats');

    // Clients
    setClients(clientRes.data ?? []);
    setClientCount(clientRes.count ?? 0);

    // Invoice aggregates
    const allInvoices = invoiceStatsRes.data ?? [];
    setInvoiceCount(allInvoices.length);
    setRevenue(
      allInvoices
        .filter((i) => i.status === 'Paid')
        .reduce((sum, i) => sum + (i.amount || 0), 0)
    );
    setPending(
      allInvoices
        .filter((i) => i.status === 'Pending' || i.status === 'Overdue')
        .reduce((sum, i) => sum + (i.amount || 0), 0)
    );

    // Recent invoices
    setInvoices(invoiceRecentRes.data ?? []);

    setLoading(false);
  }, [user.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Stat card config (computed from real data) ─────────────────────────────

  const stats = [
    {
      label:     'Total Clients',
      value:     clientCount,
      sub:       clientCount === 0 ? 'Add your first client' : `${clientCount} client${clientCount !== 1 ? 's' : ''} total`,
      trend:     clientCount > 0 ? `${clientCount} total` : 'None yet',
      trendUp:   clientCount > 0,
      icon:      HiUsers,
      iconBg:    'bg-indigo-50',
      iconColor: 'text-indigo-600',
    },
    {
      label:     'Total Invoices',
      value:     invoiceCount,
      sub:       invoiceCount === 0 ? 'No invoices yet' : `${invoiceCount} invoice${invoiceCount !== 1 ? 's' : ''} created`,
      trend:     invoiceCount > 0 ? `${invoiceCount} total` : 'None yet',
      trendUp:   invoiceCount > 0,
      icon:      HiDocumentText,
      iconBg:    'bg-green-50',
      iconColor: 'text-green-600',
    },
    {
      label:     'Revenue This Month',
      value:     formatAmount(revenue),
      sub:       revenue === 0 ? 'No paid invoices yet' : 'From paid invoices',
      trend:     revenue > 0 ? 'Paid' : '—',
      trendUp:   revenue > 0,
      icon:      HiCurrencyDollar,
      iconBg:    'bg-purple-50',
      iconColor: 'text-purple-600',
    },
    {
      label:     'Pending Payments',
      value:     formatAmount(pending),
      sub:       pending === 0 ? 'No outstanding invoices' : 'Pending + overdue',
      trend:     pending > 0 ? 'Action needed' : '—',
      trendUp:   false,
      icon:      HiClock,
      iconBg:    'bg-orange-50',
      iconColor: 'text-orange-500',
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Layout title="Dashboard">

      {/* Welcome */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">{getGreeting()} 👋</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Here's what's happening with your business today.
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-8">
        {stats.map((stat) => (
          <StatCard key={stat.label} {...stat} loading={loading} />
        ))}
      </div>

      {/* Tables */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

        {/* ── Recent Clients ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50">
            <div>
              <h3 className="font-semibold text-gray-900 text-sm">Recent Clients</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {loading ? 'Loading…' : `${clientCount} client${clientCount !== 1 ? 's' : ''} total`}
              </p>
            </div>
            <button
              onClick={() => navigate('/clients')}
              className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition"
            >
              View all <HiArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {loading ? (
            <div className="divide-y divide-gray-50">
              {[...Array(3)].map((_, i) => <RowSkeleton key={i} />)}
            </div>
          ) : clients.length === 0 ? (
            <EmptyState
              icon={HiUsers}
              title="No clients yet"
              sub="Head over to Clients to add your first one"
            />
          ) : (
            <div className="divide-y divide-gray-50">
              {clients.map((client) => (
                <div
                  key={client.id}
                  className="flex items-center gap-3 px-6 py-3.5 hover:bg-gray-50 transition cursor-pointer"
                  onClick={() => navigate('/clients')}
                >
                  <div className={`w-9 h-9 rounded-full ${avatarColor(client.name)}
                    flex items-center justify-center flex-shrink-0`}>
                    <span className="text-white font-bold text-sm">
                      {client.name[0].toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 truncate">{client.name}</p>
                    <p className="text-xs text-gray-400 truncate">{client.email}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full
                      ${CLIENT_STATUS_STYLE[client.status]}`}>
                      {client.status}
                    </span>
                    <span className="text-xs text-gray-400">{formatDate(client.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Recent Invoices ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50">
            <div>
              <h3 className="font-semibold text-gray-900 text-sm">Recent Invoices</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {loading ? 'Loading…' : `${invoiceCount} invoice${invoiceCount !== 1 ? 's' : ''} total`}
              </p>
            </div>
            <button
              onClick={() => navigate('/invoices')}
              className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition"
            >
              View all <HiArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {loading ? (
            <div className="divide-y divide-gray-50">
              {[...Array(3)].map((_, i) => <RowSkeleton key={i} />)}
            </div>
          ) : invoices.length === 0 ? (
            <EmptyState
              icon={HiDocumentText}
              title="No invoices yet"
              sub="Create your first invoice to get paid"
            />
          ) : (
            <div className="divide-y divide-gray-50">
              {invoices.map((inv) => (
                <div
                  key={inv.id}
                  className="flex items-center gap-3 px-6 py-3.5 hover:bg-gray-50 transition cursor-pointer"
                  onClick={() => navigate(`/invoices/${inv.id}`)}
                >
                  <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <HiDocumentText className="w-4 h-4 text-indigo-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-indigo-600">{inv.invoice_number}</p>
                    <p className="text-xs text-gray-400 truncate">{inv.clients?.name ?? '—'}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <p className="text-sm font-bold text-gray-900">{formatAmount(inv.amount)}</p>
                    <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full
                      ${INVOICE_STATUS_STYLE[inv.status] ?? 'bg-gray-100 text-gray-500'}`}>
                      {inv.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </Layout>
  );
}
